import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, Input, Output, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { BehaviorSubject, timer } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';

import { EntityItemsModule } from '@core/ui';
import { IndustryCatalogAssignment } from '@mp/content-manager/plattformkataloge/data-access';

import { CatalogDragPlaceholderComponent } from './catalog-drag-placeholder/catalog-drag-placeholder.component';
import { SortableCatalog } from './models';
import { SortableCatalogComponent } from './sortable-catalog/sortable-catalog.component';

type SortEvent = {
  previousIndex: number;
  currentIndex: number;
};

@Component({
  selector: 'mp-sortable-katalog-list',
  templateUrl: './sortable-katalog-list.component.html',
  styleUrl: './sortable-katalog-list.component.scss',
  imports: [
    AsyncPipe,

    MatIcon,
    DragDropModule,

    SortableCatalogComponent,
    EntityItemsModule,
    CatalogDragPlaceholderComponent,
  ],
})
export class SortableKatalogListComponent {
  @Input()
  get invertArrowKeys() {
    return this._invertArrowKeys;
  }
  set invertArrowKeys(value: BooleanInput) {
    this._invertArrowKeys = coerceBooleanProperty(value);
  }
  private _invertArrowKeys = false;

  @Input() kataloge: Array<SortableCatalog> = [];
  @Input() allowSort = true;
  @Input() allowWrite = true;
  readonly allowNavigation = input(true);
  @Input() maxIndex = 0;
  @Input() calculatedStartIndex = 0;

  @Output() readonly itemRemoved = new EventEmitter<SortableCatalog>();
  @Output() readonly itemAktualisiert = new EventEmitter<{
    katalog: IndustryCatalogAssignment;
    shouldUpdate: boolean;
  }>();
  @Output() readonly itemsAdded = new EventEmitter<Array<SortableCatalog>>();
  @Output() readonly itemSorted = new EventEmitter<{ id: string; previousIndex: number; currentIndex: number }>();
  @Output() readonly industriekatalogClicked = new EventEmitter<string>();

  private _lastSortedId$ = new BehaviorSubject(undefined as string | undefined);
  readonly lastSortedId$ = this._lastSortedId$.asObservable();

  constructor() {
    this.registerLastSortedItemHighlighting();
  }

  /** This sets up an observable that emits the last sorted Katalog-Id for 500ms. */
  private registerLastSortedItemHighlighting(): void {
    this.itemSorted
      .pipe(
        map(({ id }) => id),
        tap((id) => this._lastSortedId$.next(id)),
        mergeMap(() => timer(500)),
        tap(() => this._lastSortedId$.next(undefined)),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  handleItemDropped(event: SortEvent): void {
    const katalog = this.kataloge[event.previousIndex];
    const calculatedSortEvent: SortEvent = {
      previousIndex: event.previousIndex + this.calculatedStartIndex,
      currentIndex: event.currentIndex + this.calculatedStartIndex,
    };

    this.emitItemSorted(katalog, calculatedSortEvent);
  }

  handleItemAktualisiert({
    catalog,
    shouldUpdate,
  }: {
    catalog: IndustryCatalogAssignment;
    shouldUpdate: boolean;
  }): void {
    this.itemAktualisiert.emit({ katalog: catalog, shouldUpdate });
  }

  handleItemSortedManually(katalog: SortableCatalog, { previousIndex, currentIndex }: SortEvent): void {
    // NOTE: Since the index input does not actually contain an index but a position,
    // we need to decrease the position by one to get the correct array index:
    const event = {
      previousIndex: previousIndex - 1,
      currentIndex: currentIndex - 1,
    };

    this.emitItemSorted(katalog, event);
  }

  emitItemSorted({ catalogId }: SortableCatalog, { previousIndex, currentIndex }: SortEvent): void {
    this.itemSorted.emit({ id: catalogId, previousIndex, currentIndex });
  }

  emitItemRemoved(katalog: SortableCatalog): void {
    this.itemRemoved.emit(katalog);
  }

  emitIndustriekatalogClicked(id?: string): void {
    this.industriekatalogClicked.emit(id);
  }
}
